import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

// Create base store configuration for users
const usersBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/users",
  name: "UsersStoreRefactor",
  objectName: "Gebruiker", // Auto-generates CRUD success messages
  enableCache: true, // 5-minute cache for users
  actionCableChannel: "UserChannel", // Use class name (singular)
  infiniteLoading: false,
});

export const useUsersStoreRefactor = defineStore("usersRefactor", {
  ...usersBaseStore,

  getters: {
    ...usersBaseStore.getters,

    /**
     * Get all users (maps to state.data for compatibility)
     */
    users: (state) => {
      return state.data || [];
    },

    /**
     * Get active users only
     */
    activeUsers: (state) => {
      return (state.data || []).filter((user) => user.active === true);
    },

    /**
     * Get active users who are members of the current tenant
     */
    activeUsersInCurrentTenant: (state) => {
      return (state.data || []).filter(
        (user) =>
          user.active === true && user.member_of_current_tenant === true,
      );
    },

    /**
     * Get user by ID (enhanced with additional checks)
     */
    getUserById: (state) => (id) => {
      return state.data.find((user) => user.id == id);
    },
  },

  actions: {
    ...usersBaseStore.actions,

    /**
     * Activate a user
     * @param {Object|Number} userOrId - User object or user ID
     */
    async activate(userOrId) {
      const id = typeof userOrId === "object" ? userOrId.id : userOrId;
      const user = this.getUserById(id);
      const userName = user
        ? `${user.first_name} ${user.last_name}`
        : "gebruiker";

      return this.customApiCall(
        "GET",
        `${id}/activate`,
        {},
        `${userName} is geactiveerd`,
      );
    },

    /**
     * Deactivate a user
     * @param {Object|Number} userOrId - User object or user ID
     */
    async deactivate(userOrId) {
      const id = typeof userOrId === "object" ? userOrId.id : userOrId;
      const user = this.getUserById(id);
      const userName = user
        ? `${user.first_name} ${user.last_name}`
        : "gebruiker";

      return this.customApiCall(
        "GET",
        `${id}/deactivate`,
        {},
        `${userName} is gedeactiveerd`,
      );
    },

    /**
     * Change user password
     * @param {Object|Number} userOrId - User object or user ID
     * @param {String} newPassword - New password
     */
    async changePassword(userOrId, newPassword) {
      const id = typeof userOrId === "object" ? userOrId.id : userOrId;
      const user = this.getUserById(id);
      const userName = user
        ? `${user.first_name} ${user.last_name}`
        : "gebruiker";

      return this.customApiCall(
        "POST",
        `${id}/change_password`,
        { password: newPassword },
        `Wachtwoord voor ${userName} is bijgewerkt`,
      );
    },

    /**
     * Update user profile image
     * @param {Object|Number} userOrId - User object or user ID
     * @param {File|String} image - Image file or image URL
     */
    async updateProfileImage(userOrId, image) {
      const id = typeof userOrId === "object" ? userOrId.id : userOrId;
      const user = this.getUserById(id);
      const userName = user
        ? `${user.first_name} ${user.last_name}`
        : "gebruiker";

      const formData = new FormData();
      formData.append("profile_image", image);

      return this.customApiCall(
        "PATCH",
        `${id}/update_profile_image`,
        formData,
        `Profielfoto voor ${userName} is bijgewerkt`,
      );
    },

    /**
     * Get current user
     */
    async getCurrentUser() {
      this.loading = true;
      this.error = null;

      try {
        // Use the real API client
        const result = await apiClient.get("/api/v1/users/current");
        return result;
      } catch (error) {
        this.error = error.message || "Er is een fout opgetreden";
        console.error(
          `${this.name || "UsersStoreRefactor"}: Error getting current user:`,
          error,
        );
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // CRUD operations now fully inherited with auto-generated success messages
    // Can still override for custom messages with full names if needed:
    async create(user) {
      const userName =
        user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : null;
      if (userName) {
        return usersBaseStore.actions.create.call(
          this,
          user,
          `${userName} is succesvol aangemaakt`,
        );
      }
      return usersBaseStore.actions.create.call(this, user);
    },

    async update(user) {
      const userName =
        user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : null;
      if (userName) {
        return usersBaseStore.actions.update.call(
          this,
          user,
          `${userName} is succesvol bijgewerkt`,
        );
      }
      return usersBaseStore.actions.update.call(this, user);
    },

    async destroy(userOrId) {
      const id = typeof userOrId === "object" ? userOrId.id : userOrId;
      const user = this.getUserById(id);
      const userName =
        user?.first_name && user?.last_name
          ? `${user.first_name} ${user.last_name}`
          : null;
      if (userName) {
        return usersBaseStore.actions.destroy.call(
          this,
          userOrId,
          `${userName} is succesvol verwijderd`,
        );
      }
      return usersBaseStore.actions.destroy.call(this, userOrId);
    },
  },
});
